import type { Player } from '@/playback/api'
import type { Unsubscribe } from '@amazon/vinyl-util'
import { openInactivityModal } from '@/messaging/systemModals'

const INACTIVITY_LIMIT_MS = 2 * 60 * 60 * 1000 // 2 hours

/**
 * Ghost-listener detection: tracks user interaction and pauses playback
 * when a track ends after 2 hours of inactivity. Shows the inactivity
 * modal so the user can choose to continue or stop.
 *
 * Mirrors GH's GhostListenerStore + LocalPlaybackController inactivity
 * check. One-shot per app lifecycle (fires at most once).
 */
export function initializeInactivityMonitor(deps: {
    readonly playerPromise: Promise<Player>
}): Unsubscribe {
    let lastInteraction = Date.now()
    let fired = false
    let unsubEnded: Unsubscribe | undefined

    const resetTimer = () => {
        if (!fired) lastInteraction = Date.now()
    }

    window.addEventListener('mousedown', resetTimer)
    window.addEventListener('keydown', resetTimer)
    window.addEventListener('click', resetTimer)

    void deps.playerPromise.then((player) => {
        unsubEnded = player.on('ended', () => {
            if (fired) return
            if (Date.now() - lastInteraction < INACTIVITY_LIMIT_MS) return

            fired = true
            player.pause()
            openInactivityModal({
                onContinue: () => {
                    player.play().catch(() => {})
                },
            })
        })
    })

    return () => {
        window.removeEventListener('mousedown', resetTimer)
        window.removeEventListener('keydown', resetTimer)
        window.removeEventListener('click', resetTimer)
        unsubEnded?.()
    }
}
